// a library to wrap and simplify api calls
import apisauce from "apisauce";

// ------
// STEP 1
// ------
//
// Create and configure an apisauce-based api object.
//
const api = apisauce.create({
  // base URL is read from the "constructor"
  baseURL: process.env.REACT_APP_API_URL,
  // here are some default headers
  headers: {
    "Cache-Control": "no-cache",
  },
  // 60 second timeout...
  timeout: 60000,
});

// ------
// STEP 2
// ------
//
// Define some functions that call the api.  The goal is to provide
// a thin wrapper of the api layer providing nicer feeling functions
// rather than "get", "post" and friends.
//
// I generally don't like wrapping the output at this level because
// sometimes specific actions need to be take on `403` or `401`, etc.
//
// Since we can't hide from that, we embrace it by getting out of the
// way at this level.
//
const setAuthToken = (userAuth) => api.setHeader("Authorization", "Bearer " + userAuth);
const removeAuthToken = () => api.deleteHeader("Authorization");

const getAccount = () => api.get("api/v1/account");
const updateAccount = (account) => api.put("api/v1/account", account);
const getDefMembership = () => api.get("api/v1/memberships/accounts");
const setDefaultMembership = (membershipId) => api.put(`api/v1/memberships/account/default/${membershipId}`);
const getProfile = () => api.get("api/v1/account/profile");
const updateProfile = (profile) => api.put("api/v1/account/profile", profile);
const resendVerificationEmail = (user) => api.post("api/v1/account/resend/verification-email", user);

const getUser = (userId) => api.get("api/v1/users/" + userId);
const getUsers = (options) => api.get("api/v1/users", options);
const createUser = (user) => api.post("api/v1/users", user);
const updateUser = (user) => api.put("api/v1/users", user);
const deleteUser = (userId) => api.delete("api/v1/users/" + userId);

const getCountry = (countryId) => api.get("api/v1/countries/" + countryId);
const getCountries = (options) => api.get("api/v1/countries/graphed", options);
const createCountry = (country) => api.post("api/v1/countries", country);
const updateCountry = (country) => api.put("api/v1/countries", country);
const deleteCountry = (countryId) => api.delete("api/v1/countries/" + countryId);

const getBusiness = (businessId) => api.get("api/v1/businesses/" + businessId);
const getMerchantAgreements = (businessId, provider) =>
  api.get(`api/v1/businesses/merchant-agreement/${businessId}/recent`, {
    provider,
  });
const getBusinesses = (options) => api.get("api/v1/businesses", options);
const getBusinessSummary = (businessId) => api.get(`api/v1/businesses/${businessId}/report`);
const createBusiness = (business) => api.post("api/v1/businesses", business);
const updateBusiness = (businessId, business) => api.put("api/v1/businesses/" + businessId, business);
const deleteBusiness = (businessId) => api.delete("api/v1/businesses/" + businessId);
const closeNotification = (businessId) => api.patch(`api/v1/businesses/verification-acknowledged/${businessId}`)
const updatePspFees = (businessId) => api.patch(`api/v1/businesses/fees-passed-on/${businessId}/toggle`)
const toggleReqsEnabled = (businessId) => api.patch(`api/v1/businesses/reqs-enabled/${businessId}/toggle`);
const toggleAllowDuplicatePhone = (businessId) => api.patch(`api/v1/businesses/allow-duplicate-phone/${businessId}/toggle`);
const getSignupReqs = (businessId) => api.get(`api/v1/businesses/${businessId}/signup-reqs`);
const createSignupReqs = (businessId, tagGroupIds) =>
  api.post(`api/v1/businesses/${businessId}/signup-reqs`, tagGroupIds);
const deleteSignupReq = (businessId, signupReqId) =>
  api.delete(`api/v1/businesses/${businessId}/signup-reqs/${signupReqId}`);
const getSignupReqExclusions = (businessId) =>
  api.get(`api/v1/businesses/${businessId}/signup-req-exclusions`);
const createSignupReqExclusions = (businessId, planGroupIds) =>
  api.post(`api/v1/businesses/${businessId}/signup-req-exclusions`, planGroupIds);
const deleteSignupReqExclusion = (businessId, exclusionId) =>
  api.delete(`api/v1/businesses/${businessId}/signup-req-exclusions/${exclusionId}`);
const getBusinessQr = (businessId, qr) => api.get(`api/v1/businesses/${businessId}/barcode/qr.png`, qr, {
  headers: {
    'Accept': '*/*',
  },
  responseType: "arraybuffer"
});
const getIndustries = (industry) => api.get("api/v1/industries", industry)

const getPlan = (planId) => api.get("api/v1/plans/" + planId);
const getPlanQr = (planId) => api.get(`api/v1/plans/${planId}/barcode/qr.png`, {}, {
  headers: {
    Accept: '*/*',
  },
  responseType: 'arraybuffer',
});
const getPlans = (businessId, options) => api.get("api/v1/plans?businessId.equals=" + businessId, options);
const createPlan = (plan) => api.post("api/v1/plans", plan);
const updatePlan = (planId, plan) => api.patch(`api/v1/plans/${planId}`, plan);
const changePlanPrice = (planId, amount) => api.put(`api/v1/plans/${planId}/change-amount`, amount);
const deletePlan = (planId) => api.delete("api/v1/plans/" + planId);
const archivePlan = (planId, membershipId) => api.patch(`api/v1/plans/archive/${planId}/${membershipId}`);
const planPerformance = (tierId) => api.get(`api/v1/plans/performance/${tierId}`);
const migratePlan = (migratedData) => api.post(`api/v1/plan-migrations`, migratedData);
const getPlanPaymentMissed = (planId, options) => api.get(`api/v1/payment-schedules/plan/${planId}/missed`, options);
const getPlanPaymentMissedCount = (planId) => api.get(`api/v1/payment-schedules/plan/${planId}/missed/count`);
const getPlanPaymentPending = (planId, options) => api.get(`api/v1/payment-schedules/plan/${planId}/pending`, options);
const getPlanPaymentPendingCount = (planId) => api.get(`api/v1/payment-schedules/plan/${planId}/pending/count`);
const getPlanPaymentCollected = (planId, options) => api.get("api/v1/payment-tracks?planId.equals=" + planId, options);
const getPlanPaymentCollectedCount = (planId) => api.get("api/v1/payment-tracks/count?planId.equals=" + planId);
const getPaymentTrackCount = (options) => api.get("api/v1/payment-tracks/count", options);
const addProductWatcher = (watcher) => api.post("/api/v1/watchers", watcher);
const removeProductWatcher = (watcherId) => api.delete("/api/v1/watchers/" + watcherId);
const productWatcherCount = (planGroupId) => api.get(`/api/v1/watchers/plan-group/${planGroupId}/count`);
const productWatchers = (planGroupId) => api.get(`/api/v1/watchers/plan-group/${planGroupId}`);


const getCustomer = (customerId) => api.get("api/v1/customers/" + customerId);
const getCustomers = (options) => api.get("api/v1/customers", options);
const createCustomer = (customer) => api.post("api/v1/customers", customer);
const updateCustomer = (customer) => api.put("api/v1/customers", customer);
const deleteCustomer = (customerId) => api.delete("api/v1/customers/" + customerId);

const getSubscriber = (subscriberId) => api.get("api/v1/subscribers/" + subscriberId);
const getSubscribers = (businessId, options) => api.get(`api/v1/_search/subscribers/${businessId}?searchText`, options);
const getCountSubscribers = (businessId) => api.get("api/v1/subscribers/count?businessId.equals=" + businessId);
const getCountSubscriptions = (options) => api.get("api/v1/subscribers/count", options);
const getPlanSubscribersCount = (planId) => api.get("/api/v1/subscribers/count?planId.equals=" + planId);
const getPlanSubscribers = (planId, options) => api.get("api/v1/subscribers?planId.equals=" + planId, options);
const createSubscriber = (subscriber) => api.post("api/v1/subscribers", subscriber);
const updateSubscriber = (subscriber) => api.put("api/v1/subscribers", subscriber);
const deleteSubscriber = (subscriberId) => api.delete("api/v1/subscribers/" + subscriberId);
const addBulkListToSegment = (audienceId, segmentId) =>
  api.post(`api/v1/audiences/${audienceId}/apply-segment/${segmentId}`)
const createSegment = (segment) => api.post("api/v1/segments", segment);
const updateSegment = (segmentId, segment) => api.put(`api/v1/segments/${segmentId}`, segment);
const getSegment = (businessId, options) =>
  api.get(`api/v1/segments/business/${businessId}`, options);
const searchSegmentsByName = (businessId, name) =>
  api.get(`api/v1/segments/${businessId}/search/${encodeURIComponent(name)}`);
const getSegmentCount = (businessId) => api.get(`api/v1/segments/business/${businessId}/count`);
const addSingleSubscriberToSegment = (segmentId, bcId) => api.post(`api/v1/segments/${segmentId}/associate/${bcId}`);
const addBulkSubscriberToSegment = (segmentId, bcIds) => api.post(`api/v1/segments/${segmentId}/associate/bulk`, { ids: bcIds });
const removeSubscriberFromSegment = (bcId) => api.delete(`api/v1/segments/dissociate/${bcId}`)
const removeBulkSubscribersFromSegment = (bcIds) => api.delete('api/v1/segments/dissociate/bulk', {}, { data: { ids: bcIds } });
const rescheduleSubscriber = (subscriberId) => api.patch("api/v1/subscribers/reschedule/" + subscriberId);
const checkExclusivity = (newPlanId, msisdn) => api.get("api/v1/subscribers/check-exclusivity/" + newPlanId + "/" + msisdn);
const substituteSubscribersFilter = (query, filters, options) => api.get("api/v1/_search/subscribers?query=" + query + "&filters=" + filters, options);
const searchSubscribers = (businessId) => api.get(`api / v1 / _search / subscribers / ${businessId}?searchText`);
const countStatusSubscribers = (businessId, query) => api.get(`api / v1 / _search / subscribers / ${businessId}/count?${query}`);
const filterSubscribers = (query, businessId, options) => api.get(`api/v1/_search/subscribers/${businessId}?searchText${query}`, options);
const getCountSearchSubscribers = (query, businessId) => api.get(`api/v1/_search/subscribers/${businessId}/count?searchText=${query}`);
const dateFilterSubscribers = (query, businessId, startDate, endDate, options) => api.get("api/v1/_search/subscribers/" + businessId + "/" + startDate + "/" + endDate + query, options);
const getCountDateSearchSubscribers = (query, businessId, startDate, endDate) => api.get(`api/v1/_search/subscribers/${businessId}/${startDate}/${endDate}/count${query}`);
const countSubscribers = (query, filters) => api.get("api/v1/_search/subscribers/count?query=" + query + "&filters=" + filters);
const getSubscriberMissedSchedules = (subscriberId, missedByDate) => api.get("api/v1/payment-schedules/subscriber/" + subscriberId + "/missed/" + missedByDate);
const subscribersBulkCancel = (businessId, membershipId, ids) => api.patch(`api/v1/subscribers/${businessId}/bulk/cancel/${membershipId}`, { ids, });
const subscribersBulkPause = (businessId, membershipId, ids) => api.patch(`api/v1/subscribers/${businessId}/bulk/pause/${membershipId}`, { ids });
const businessCustomersBulkPause = (membershipId, ids) => api.patch(`api/v1/business-customers/subscriptions/bulk-pause/${membershipId}`, { ids })
const businessCustomersBulkCancel = (membershipId, ids) => api.patch(`api/v1/business-customers/subscriptions/bulk-cancel/${membershipId}`, { ids })
const businessCustomersSingleCancel = (businessCustomerId, membershipId) => api.patch(`api/v1/business-customers/${businessCustomerId}/subscriptions/cancel/${membershipId}`)
const businessCustomersSinglePause = (businessCustomerId, membershipId) => api.patch(`api/v1/business-customers/${businessCustomerId}/subscriptions/pause/${membershipId}`)
const customerReceivedPayments = (businessCustomerId, options) => api.get(`/api/v1/business-customers/${businessCustomerId}/subscriptions/summary`, options);
const businessCustomerMissedPayments = (businessCustomerId) => api.get(`/api/v1/business-customers/${businessCustomerId}/subscriptions/missed`);
const customerPendingPayments = (businessCustomerId) => api.get(`/api/v1/business-customers/${businessCustomerId}/subscriptions/pending`);
const externalPaymentBySubscriber = (planId, membershipId, formData) => api.post(`api/v1/payment-schedules/plan/${planId}/mark-externally-paid/${membershipId}`, formData);
const externalPaymentPerSubscription = (subscriptionId, membershipId, formData) => api.post(`api/v1/payment-schedules/subscriber/${subscriptionId}/mark-externally-paid/${membershipId}`, formData);
const triggerMomoMembershipQuery = (membershipId) =>
  membershipId != null && membershipId !== "" ? `?membershipId=${membershipId}` : "";
const triggerMomoPayment = (scopePath, businessCustomerId, membershipId, formData) =>
  api.post(`api/v1/payment-schedules/${scopePath}/trigger-momo-payment/${businessCustomerId}${triggerMomoMembershipQuery(membershipId)}`, formData);
const triggerMomoPaymentByPlan = (planId, businessCustomerId, membershipId, formData) =>
  triggerMomoPayment(`plan/${planId}`, businessCustomerId, membershipId, formData);
const triggerMomoPaymentBySchedule = (scheduleId, businessCustomerId, membershipId, formData) =>
  triggerMomoPayment(scheduleId, businessCustomerId, membershipId, formData);
const triggerMomoPaymentBySubscriber = (subscriberId, businessCustomerId, membershipId, formData) =>
  triggerMomoPayment(`subscriber/${subscriberId}`, businessCustomerId, membershipId, formData);
const fetchProviderAccount = (provider, businessId, formData) =>
  api.patch(`api/v1/providers/${provider}/fetch-account/${businessId}`, formData);
const verifyPaymentRequest = (paymentRequestId) =>
  api.patch(`api/v1/payment-requests/verify/${paymentRequestId}`);
const getCustomerReport = (businessId, options) => api.get(`api/v1/businesses/${businessId}/business-customers/summary`, options);
const getSubscriptionSummary = (subscriptionId, options) => api.get(`api/v1/subscribers/${subscriptionId}/summary`, options);
const getCustomerCharts = (businessId, duration, options) => api.get(`api/v1/businesses/${businessId}/business-customers/${duration}/series`, options);

const getMembership = (membershipId) => api.get("api/v1/memberships/" + membershipId);
const getMemberships = (businessId, options) => api.get("api/v1/memberships?businessId.equals=" + businessId, options);
const getColleagues = (businessId, options) => api.get(`api/v1/memberships/${businessId}/colleagues`, options);
const createMembership = (membership) => api.post("api/v1/memberships", membership);
const signupBusiness = (signup) => api.post("api/v1/memberships/account/default", signup);
const updateMembership = (membership) => api.put(`api/v1/memberships/${membership.id}`, membership);
const deleteMembership = (membershipId) => api.delete("api/v1/memberships/" + membershipId);

const getPaySwitchCredentials = (businessId, provider) => api.get(`api/v1/providers/credential/${businessId}/provider/${provider}`)
const getAutoBillingSupport = (businessId) => api.get(`api/v1/providers/auto-billing/${businessId}`);
const createPaySwitchCredentials = (businessId, credentials) => api.post(`api/v1/providers/credential/${businessId}/provider`, credentials);
const uploadProviderPrivateKey = (businessId, provider, file) => api.post(`api/v1/providers/credential/${businessId}/provider/${provider}/private-key`, file, {
  headers: {
    "content-type": "multipart/form-data",
  },
});
const updatePaySwitchMerchantId = (businessId, credentials) => api.patch(`api/v1/providers/payswitch/merchantId/${businessId}`, credentials);
const updatePaySwitchCredentials = (businessId, credentials) => api.patch(`api/v1/providers/payswitch/credential/${businessId}`, credentials);
const getPaymentCredentials = (businessId) => api.get(`/api/v1/businesses/payment-credentials/${businessId}/configured`);
const generateVerificationCode = (generationRequest) => api.post("api/v1/self-signups", generationRequest);
const getMembershipInvite = (membershipInviteId) => api.get("api/v1/membership-invites/" + membershipInviteId);
const getMembershipInvites = (businessId, options) => api.get("api/v1/membership-invites?businessId.equals=" + businessId + "&status.equals=PENDING", options);
const createMembershipInvite = (membershipInvite) => api.post("api/v1/membership-invites", membershipInvite);
const updateMembershipInvite = (membershipInvite) => api.put("api/v1/membership-invites", membershipInvite);
const deleteMembershipInvite = (membershipInviteId) => api.delete("api/v1/membership-invites/" + membershipInviteId);
const resendMembershipInvite = (membershipInviteId) => api.patch(`api/v1/membership-invites/${membershipInviteId}/resend`);

const getSelfSignup = (selfSignupId) => api.get("api/v1/self-signups/" + selfSignupId);
const resendSelfSignup = (id) => api.patch("api/v1/self-signups/resend/" + id);
const resendBulkSelfSignup = (invitations) => api.patch("api/v1/self-signups/resend/", invitations);
const getSelfSignups = (businessId, options) => api.get(`api/v1/_search/self-signups/${businessId}`, options);
const searchInvitations = (businessId, filter, options) => api.get(`api/v1/_search/self-signups/${businessId}`, filter, options);
const getCountSelfSignups = (businessId) => api.get(`api/v1/_search/self-signups/${businessId}/count`);
const searchInvitationsCount = (businessId, filter) => api.get(`api/v1/_search/self-signups/${businessId}/count?`, filter);
const getSelfSignupsFilter = (businessId, filter, options) => api.get(`api/v1/self-signups?businessId.equals=${businessId} &${filter}`, options);
const deleteSelfSignup = (selfSignupId) => api.delete("api/v1/self-signups/" + selfSignupId);
const downloadUptakePdf = (businessId, planId, segmentId, tags) => {
  const params = [];
  if (segmentId) params.push(`segmentId=${segmentId}`);
  if (tags) params.push(`tags=${tags}`);
  const queryString = params.length > 0 ? `?${params.join('&')}` : '';
  return api.get(`api/v1/invitation-uptakes/${businessId}/${planId}/pdf${queryString}`, {},
    {
      responseType: 'blob',
    }
  );
}
const downloadUptakeCsv = (businessId, planId, segmentId, tags) => {
  const params = [];
  if (segmentId) params.push(`segmentId=${segmentId}`);
  if (tags) params.push(`tags=${tags}`);
  const queryString = params.length > 0 ? `?${params.join('&')}` : '';
  return api.get(`api/v1/invitation-uptakes/${businessId}/${planId}/csv${queryString}`, {},
    {
      headers: {
        'Accept': '*/*',
      },
      'Content-Type': "text/csv"
    }
  );
}
const getInvitationUptake = (businessId, planId, query, options) => api.get("api/v1/invitation-uptakes/" + businessId + "/" + planId + query, options);

const getPaymentSchedule = (paymentScheduleId) => api.get("api/v1/payment-schedules/" + paymentScheduleId);
const getPaymentSchedules = (subscriberId, options) => api.get("api/v1/payment-schedules?subscriptionId.equals=" + subscriberId, options);
const getPaymentScheduleRange = (businessId, startDate, endDate, options) => api.get("api/v1/payment-schedules/business/" + businessId + "/" + startDate + "/" + endDate, options);
const calendarQueryParams = ({ startDate, endDate, groupId, planId, bcId, businessCustomerId, ...options } = {}) => ({
  ...(startDate ? { startDate } : {}),
  ...(endDate ? { endDate } : {}),
  ...(groupId ? { groupId } : {}),
  ...(planId ? { planId } : {}),
  ...(bcId ? { bcId } : {}),
  ...(businessCustomerId ? { businessCustomerId } : {}),
  ...options,
});
const getPaymentScheduleCalendar = (businessId, params) =>
  api.get(`api/v1/payment-schedules/${businessId}/calendar`, calendarQueryParams(params));
const getPaymentScheduleCalendarDay = (businessId, date, params) =>
  api.get(`api/v1/payment-schedules/${businessId}/calendar/${date}`, calendarQueryParams(params));
const getPaymentScheduleSummary = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/report/${startDate}/${endDate}`);
const getWeeklyPaymentScheduleSummary = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/report/${startDate}/${endDate}`);
const get3MonthsPaymentScheduleSummary = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/report/${startDate}/${endDate}`);
const get6MonthsPaymentScheduleSummary = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/report/${startDate}/${endDate}`);
const get12MonthsPaymentScheduleSummary = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/report/${startDate}/${endDate}`);
const getPaymentScheduleProjection = (businessId, startDate, endDate) => api.get("api/v1/payment-schedules/projection/business/" + businessId + "/" + startDate + "/" + endDate);
const createPaymentSchedule = (paymentSchedule) => api.post("api/v1/payment-schedules", paymentSchedule);
const updatePaymentSchedule = (paymentSchedule) => api.put("api/v1/payment-schedules", paymentSchedule);
const reassignPaymentSchedulePlan = (paymentScheduleId, planId, membershipId) =>
  api.put(`api/v1/payment-schedules/${paymentScheduleId}/reassign-plan/${planId}/${membershipId}`);
const deletePaymentSchedule = (paymentScheduleId) => api.delete("api/v1/payment-schedules/" + paymentScheduleId);
const notifyPaymentSchedule = (paymentScheduleId) => api.post("api/v1/payment-schedules/notify/" + paymentScheduleId);
const getSubscriptionMissedSchedule = (subscriberId, options) => api.get(`api/v1/payment-schedules?subscriptionId.equals=${subscriberId}&status.equals=EXPIRED`, options);
const getSubscriptionMissedScheduleCount = (subscriberId, options) => api.get(`api/v1/payment-schedules/count?subscriptionId.equals=${subscriberId}&status.equals=EXPIRED`, options);
const getSubscriptionPendingSchedule = (subscriberId, options) => api.get(`api/v1/payment-schedules/paginate?businessCustomerId.equals=${subscriberId}&status.equals=PENDING`, options);
const getRecentPendingPaymentScheduleForSubscription = (subscriptionId) =>
  api.get(`api/v1/payment-schedules/subscriber/${subscriptionId}/pending/recent`);
const cancelSubscription = (subscriptionId, membershipId) => api.patch(`api/v1/subscribers/${subscriptionId}/cancel/${membershipId}`);
const changeSubscriptionTarget = (subscriptionId, membershipId, amount) =>
  api.put(`api/v1/subscribers/${subscriptionId}/change-target/${membershipId}`, { amount });
const pauseSubscription = (subscriptionId, membershipId) => api.patch(`api/v1/subscribers/${subscriptionId}/pause/${membershipId}`);
const resumeSubscription = (id, startFrom, membershipId, action) => api.patch(`api/v1/subscribers/reschedule/${id}/${startFrom}/${membershipId}?action=${action}`);
const rescheduleSubscription = (subscriberId, startFrom, membershipId) => api.patch(`api/v1/subscribers/reschedule/${subscriberId}/${startFrom}/${membershipId}`);
const getSubscriberRevenue = (subscriberId) => api.get(`api/v1/subscribers/${subscriberId}/revenue`)
const getMissedPaymentCSV = (businessId, query, options) => api.get(`/api/v1/payment-schedules/${businessId}/missed/csv?${query}`, options,
  {
    headers: {
      'Accept': '*/*',
    },
    'Content-Type': "text/csv"
  }
);
const getMissedPaymentPDF = (businessId, membershipId, query, options) => api.get(`/api/v1/payment-schedules/${businessId}/${membershipId}/missed/pdf?${query}`, options,
  {
    responseType: 'blob',
  }
);

const getExternalPaymentCSV = (businessId, query, options) => api.get(`/api/v1/payment-schedules/${businessId}/external/csv?${query}`, options,
  {
    headers: {
      'Accept': '*/*',
    },
    'Content-Type': "text/csv"
  }
);
const getExternalPaymentPDF = (businessId, membershipId, query, options) => api.get(`/api/v1/payment-schedules/${businessId}/${membershipId}/external/pdf?${query}`, options,
  {
    responseType: 'blob',
  }
);

const getPaymentTrack = (paymentTrackId) => api.get(`api/v1/payment-tracks/${paymentTrackId}`);
const getPaymentTrackGroup = (planGroupId, options) => api.get("api/v1/payment-tracks?planGroupId.equals=" + planGroupId, options);
const getCollectedPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-tracks/${businessId}${filter}`, options);
const getMissedPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/missed${filter}`, options);
const getExternalPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/external${filter}`, options);
const getPaymentTrackGroupCount = (planGroupId, options) => api.get("api/v1/payment-tracks/count?planGroupId.equals=" + planGroupId, options);
const getPaymentTracks = (subscriberId, options) => api.get("api/v1/payment-tracks?subscriberId.equals=" + subscriberId, options);
const getPaymentTrackRange = (businessId, startDate, endDate, options) => api.get("api/v1/payment-tracks/business/" + businessId + "/" + startDate + "/" + endDate, options);
const getPaymentTrackRevenue = (businessId, startDate, endDate) => api.get("api/v1/payment-tracks/revenue/business/" + businessId + "/" + startDate + "/" + endDate);
const updatePaymentTrack = (paymentTrack) => api.put("api/v1/payment-tracks", paymentTrack);
const deletePaymentTrack = (paymentTrackId) => api.delete("api/v1/payment-tracks/" + paymentTrackId);
const paymentMarkExternally = (scheduleId, membershipId, schedule) => api.post(`api/v1/payment-schedules/${scheduleId}/mark-externally-paid/${membershipId}`, schedule);
const countPaymentTracks = (subscriberId, options) => api.get(`api/v1/payment-tracks/count?subscriberId.equals=${subscriberId}`, options);
const getCollectedPaymentCSV = (businessId, membershipId, query, options) => api.get(`api/v1/payment-tracks/${businessId}/${membershipId}/csv?${query}`, options,
  {
    headers: {
      'Accept': '*/*',
    },
    'Content-Type': "text/csv"
  }
);
const getCollectedPaymentPDF = (businessId, membershipId, query, options) => api.get(`api/v1/payment-tracks/${businessId}/${membershipId}/pdf?${query}`, options,
  {
    responseType: 'blob',
  }
);
const getPaymentsSummaryPdf = (businessId, membershipId, startDate, endDate) =>
  api.get(
    `/api/v1/payment-tracks/${businessId}/${membershipId}/summary/${startDate}/${endDate}/pdf`,
    {},
    { responseType: 'blob' }
  );
const getReconciliationSummaryPdf = (businessId, membershipId, startDate, endDate) =>
  api.get(
    `/api/v1/payment-tracks/${businessId}/${membershipId}/reconciliation/${startDate}/${endDate}/pdf`,
    {},
    { responseType: 'blob' }
  );
const getPaymentAttributions = (paymentTrackId) => api.get(`api/v1/payment-tracks/${paymentTrackId}/attributions`)
const getFilteredDataTotals = (businessId, filter, options) => api.get(`api/v1/payment-tracks/${businessId}/totals${filter}`, options)

const getAccountsPaymentSearch = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/accounts${filter}`, options);
const getAccountsPaymentCSV = (businessId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/accounts/csv?${query}`, options, {
  headers: { 'Accept': '*/*' },
  'Content-Type': "text/csv"
});
const getAccountsPaymentPDF = (businessId, membershipId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/${membershipId}/accounts/pdf?${query}`, options, {
  responseType: 'blob',
});
const getAccountsPaymentTotals = (businessId, filter, options) => api.get(`api/v1/payment-schedules/${businessId}/accounts/totals${filter}`, options);
const getExternalPaymentTotals = (businessId, filter, options) => api.get(`api/v1/payment-schedules/${businessId}/external/totals${filter}`, options);


const getSettlement = (settlementId) => api.get("api/v1/settlements/" + settlementId);
const getSettlementSources = (settlementId) => api.get("api/v1/settlements/sources/settlement/" + settlementId);
const getSettlementSourcesGroup = (planGroupId) => api.get("api/v1/settlements/sources/plan-group/" + planGroupId);
const getSettlements = (walletId, options) => api.get("api/v1/settlements?walletId.equals=" + walletId, options);
const getSettlementsCount = (walletId) => api.get("api/v1/settlements/count?walletId.equals=" + walletId);
const getAllSettlements = (businessId, filter) => api.get("api/v1/_search/settlements/" + businessId + filter);
const getAllSettlementsCount = (businessId, filter) => api.get(`api/v1/settlements/${businessId}/graph/count${filter}`);
const getSettlementsStatusCount = (businessId, status) => api.get("api/v1/settlements/count?walletId.equals=" + businessId + "&status.equals=" + status);
const createSettlement = (settlement) => api.post("api/v1/settlements", settlement);
const updateSettlement = (settlement) => api.put("api/v1/settlements", settlement);
const deleteSettlement = (settlement) => api.delete("api/v1/settlements/" + settlement);
const getPlanSettlements = (id) => api.get(`api/v1/settlements/sources/plan-group/${id}`);
const getStructureSettlement = (countryId) => api.get(`api/v1/settlements/config/${countryId}`)

const getPlanGroup = (planGroupId) => api.get("api/v1/plan-groups/" + planGroupId);
const getPlanGroupQr = (planGroupId) => api.get(`api/v1/plan-groups/${planGroupId}/barcode/qr.png`, {}, {
  headers: {
    Accept: '*/*',
  },
  responseType: 'arraybuffer',
});
const getPlanGroups = (businessId, options) => api.get(`api/v1/plan-groups?businessId.equals=${businessId}`, options);
const getPlanGroupsFilter = (businessId, name, options) => api.get(`api/v1/plan-groups?businessId.equals=${businessId}&name.equals=${name}`, options);
const getPlanGroupsCount = (businessId, options) => api.get("/api/v1/plan-groups/count?businessId.equals=" + businessId, options);
const getPlanGroupsGraphed = (businessId, options) => api.get("api/v1/plan-groups/graphed/" + businessId, options);
const getAllPlanGroupsGraphed = (businessId) => api.get(`api/v1/plan-groups/graphed/all/${businessId}`);
const getPlanGroupsGraphedActive = (businessId) => api.get("api/v1/plan-groups/graphed/active/" + businessId);
const getPlanGroupsGraphedArchived = (businessId) => api.get("api/v1/plan-groups/graphed/archived/" + businessId);
const getPlanGroupTier = (planGroupId, options) => api.get(`api/v1/plans?groupId.equals=${planGroupId}`, options);
const getPlanBusinessPerformance = (businessId) => api.get(`api/v1/businesses/plans/${businessId}/performance`);
const getPlanGroupPerformance = (planGroupId, options) => api.get(`api/v1/plan-groups/performance/${planGroupId}`, options);
const getPlanGroupPlansSummary = (planGroupId, options) => api.get(`api/v1/plan-groups/plans/summary/paginate/${planGroupId}`, options);
const getPlanGroupCustomersSummary = (planGroupId, options) => api.get(`api/v1/plan-groups/customers/summary/${planGroupId}`, options);
const getPlanGroupDuplicate = (id, name) => api.post(`api/v1/plan-groups/copy`, { id, name });
const getPlanReport = (businessId, options) => api.get(`api/v1/businesses/${businessId}/plan-groups/summary`, options);
const getPlanReportChart = (businessId, type, options) => api.get(`api/v1/businesses/${businessId}/plan-groups/${type}/series`, options);
const enablePlanReport = (planGroupId, report) => api.post(`api/v1/plan-groups/reportable/${planGroupId}`, report);
const leaderBoard = (planGroupId) => api.post(`api/v1/plan-groups/generate-board/${planGroupId}`);
const removeLeaderBoard = (planGroupId) => api.post(`api/v1/plan-groups/remove-board/${planGroupId}`);

const getPlanGroupPaymentMissed = (planGroupId, options) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/missed`, options);
const getPlanGroupPaymentMissedCount = (planGroupId) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/missed/count`);
const getPlanGroupPaymentPending = (planGroupId, options) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/pending`, options);
const getPlanGroupPaymentPendingCount = (planGroupId) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/pending/count`);
const getPlanGroupPaymentPaid = (planGroupId, options) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/paid`, options);
const getPlanGroupPaymentPaidCount = (planGroupId) => api.get(`api/v1/payment-schedules/plan-group/${planGroupId}/pending/count`);

const createPlanGroup = (planGroup) => api.post("api/v1/plan-groups", planGroup);
const updatePlanGroup = (planGroupId, planGroup) => api.put(`api/v1/plan-groups/${planGroupId}`, planGroup);
const deletePlanGroup = (planGroupId) => api.delete("api/v1/plan-groups/" + planGroupId);
const archivePlanGroup = (planGroupId, username) => api.patch("api/v1/plan-groups/archive/" + planGroupId + "/" + username);

const getPayoutChannel = (payoutChannelId) => api.get("api/v1/payout-channels/" + payoutChannelId);
const getPayoutChannels = (businessId, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE", options);
const getFilteredPayoutChannels = (businessId, query, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE&temporary.equals=false&identifier equals=" + query, options);
const getSavedPayoutChannels = (businessId, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE&temporary.equals=false&", options);
const getFavouriteChannels = (businessId, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE&temporary.equals=false&favourite.equals=true", options);
const setFavouritePayoutChannels = (channelId) => api.patch("api/v1/payout-channels/favourite/" + channelId);
const setUnfavouritePayoutChannels = (channelId) => api.patch("api/v1/payout-channels/unfavourite/" + channelId);
const filterBanks = (businessId, query, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE&temporary.equals=false&systemBankId.equals=" + query, options);

const createPayoutChannel = (payoutChannel) => api.post("api/v1/payout-channels", payoutChannel);
const updatePayoutChannel = (payoutChannel) => api.put("api/v1/payout-channels", payoutChannel);
const deletePayoutChannel = (payoutChannelId) => api.delete("api/v1/payout-channels/" + payoutChannelId);
const archivePayoutChannel = (payoutChannelId, username) => api.patch("api/v1/payout-channels/archive/" + payoutChannelId + "/" + username);

const getBank = (bankId) => api.get("api/v1/system-banks/" + bankId);
const getBanks = (countryId) => api.get("api/v1/system-banks?countryId.equals=" + countryId + "&active.equals=true");
const getLinkedSystemBanks = (provider, type) => api.get(`api/v1/system-banks/linked/${provider}/${type}`);

const getBankBranch = (bankId) => api.get("api/v1/system-banks-branches/" + bankId);
const getBankBranches = (bankId, options) => api.get("api/v1/system-banks-branches?bankId.equals=" + bankId, options);

const getCredentials = (businessId) => api.get("api/v1/credential/" + businessId);
const createCredentials = (businessId) => api.post("api/v1/credential/" + businessId);
const generateRequest = (businessId) => api.patch("api/v1/credential/rotate/" + businessId);
const getWebhook = (businessId) => api.get(`api/v1/webhooks/business/${businessId}`);
const getWebhookEventTypes = () => api.get("api/v1/webhooks/event-types");
const createWebhook = (businessId, url, interests, authHeaderName, authHeaderValue) => api.post("api/v1/webhooks", {
  business: { id: businessId },
  url,
  interests,
  authHeaderName,
  authHeaderValue,
});
const updateWebhook = (webhookId, businessId, url, interests, authHeaderName, authHeaderValue) => api.put(`api/v1/webhooks/${webhookId}`, {
  id: webhookId,
  business: { id: businessId },
  url,
  interests,
  authHeaderName,
  authHeaderValue,
});
const toggleWebhook = (webhookId) => api.patch(`api/v1/webhooks/${webhookId}/toggle`);

const getDocuments = (countryId) => api.get("api/v1/identity-documents?countryId.equals=" + countryId + "&active.equals=true&appliesTo.equals=INDIVIDUAL");
const getOrganisationDocuments = (countryId) => api.get("api/v1/identity-documents?countryId.equals=" + countryId + "&active.equals=true&appliesTo.equals=ORGANISATION");
const createBusinessVerification = (business, date) => api.post("api/v1/identity-verif-requests", business);
const verifyDocument = (requestId, membershipId, documentId, referenceStr, file
) => api.post(`api/v1/identity-proofs/upload/${requestId}/${membershipId}/${documentId}?reference=${referenceStr ? referenceStr : ""}`, file, {
  headers: {
    "content-type": "multipart/form-data",
  },
}
);
const uploadLogo = (businessId, file) => api.post(`api/v1/businesses/logo/${businessId}/upload`, file, {
  headers: {
    "content-type": "multipart/form-data",
  },
})
const reviewDocument = (requestId) => api.patch("api/v1/identity-verif-requests/" + requestId + "/complete");
const previewDocument = (proofId) => api.get("api/v1/identity-proofs/" + proofId + "/image");
const businessVerificationRequest = (businessId) => api.get(`api/v1/identity-verif-requests/count?businessId.equals=${businessId}&status.equals=PENDING`);

const getBusinessCustomers = (businessId, options) => api.get(`api/v1/business-customers/_search/${businessId}/simple`, options);
const getExactFilteredBusinessCustomers = (businessId, query, options) => api.get(`api/v1/business-customers/_search/${businessId}/exact/${query}`, options)
const getFilteredBusinessCustomers = (businessId, query) => api.get(`api/v1/business-customers/_search/${businessId}/simple?searchText=${query}`);
const updateEnterpriseCustomers = (businessCustomerId, businessCustomer) => api.put(`api/v1/business-customers/update/simple/${businessCustomerId}`, businessCustomer);
const getBusinessCustomerSubscriptions = (businessCustomerId) => api.get(`api/v1/subscribers/business-customer/${businessCustomerId}`);
const getBusinessCustomerSubscriptionsCount = (businessCustomerId) => api.get(`api/v1/subscribers/business-customer/${businessCustomerId}/count`);
const getBusinessCustomerStatement = (businessCustomerId, startDate, endDate, options) =>
  api.get(
    `api/v1/subscribers/business-customer/${businessCustomerId}/statement/${startDate}/${endDate}`,
    options
  );
const getBusinessCustomerStatementCsv = (businessCustomerId, startDate, endDate) =>
  api.get(
    `api/v1/subscribers/business-customer/${businessCustomerId}/statement/${startDate}/${endDate}/csv`,
    {},
    {
      headers: { Accept: "*/*" },
      "Content-Type": "text/csv",
    }
  );
const getBusinessCustomerStatementPdf = (businessCustomerId, startDate, endDate) =>
  api.get(
    `api/v1/subscribers/business-customer/${businessCustomerId}/statement/${startDate}/${endDate}/pdf`,
    {},
    { responseType: "blob" }
  );
const getBusinessCustomerRecord = (businessCustomerId) => api.get(`api/v1/business-customers/${businessCustomerId}`);
const businessCustomerImport = (businessId, audienceId, file) =>
  api.post(
    `api/v1/customer-imports/upload/${businessId}?audienceId=${encodeURIComponent(audienceId)}`,
    file
  );
const businessCustomerCsvDownload = () => api.get('api/v1/customer-imports/template/download', {}, {
  headers: { 'Accept': "text/csv" },
});
const sendBulkInvite = (businessId, inviteData) => api.post(`api/v1/customer-imports/invite/${businessId}`, inviteData);
const createBusinessCustomer = (businessId, inviteData) => api.post(`api/v1/business-customers/${businessId}/create`, inviteData);

const getCustomerListInvitation = (targetListId) => api.get(`api/v1/customer-imports/bulk-invitations/${targetListId}`);
const resendBulkInvitation = (id) => api.post(`api/v1/customer-imports/bulk-invitations/resubmit/${id}`)
const getBusinessCustomerImports = (businessId, options) => api.get("api/v1/customer-imports?businessId.equals=" + businessId, options);
const getBusinessCustomerImportsCount = (businessId) => api.get("api/v1/customer-imports/count?businessId.equals=" + businessId);
const getCustomerEnrichments = (businessId, options) =>
  api.get(`api/v1/business-customers/${businessId}/enrich`, options);
const getCustomerEnrichmentItems = (businessId, requestId, options) =>
  api.get(`api/v1/business-customers/${businessId}/enrich/${requestId}/items`, options);
const submitCustomerEnrichment = (businessId, file) =>
  api.post(`api/v1/business-customers/${businessId}/enrich`, file, {
    headers: {
      "content-type": "multipart/form-data",
    },
  });
const createAudience = (audience) => api.post("api/v1/audiences", audience);
const getAudienceById = (audienceId) => api.get(`api/v1/customer-imports/${audienceId}`);
const getAudienceBulkInvitations = (audienceId) =>
  api.get(`api/v1/audiences/bulk-invitations/${audienceId}`);
const getAudienceMembers = (audienceId, options) =>
  api.get(`api/v1/audiences/${audienceId}/members`, options);
const getAudienceBatches = (audienceId) =>
  api.get(`api/v1/audiences/${audienceId}/batches`);
const addAudienceMember = (audienceId, businessCustomerId) =>
  api.post(`api/v1/audiences/${audienceId}/members/${businessCustomerId}`);
const removeAudienceMember = (audienceId, memberId) =>
  api.delete(`api/v1/audiences/${audienceId}/members/${memberId}`);
const getAudienceCandidateSegments = (audienceId) =>
  api.get(`api/v1/audiences/${audienceId}/candidate-segments`);
const getAudienceCandidateTags = (audienceId) =>
  api.get(`api/v1/audiences/${audienceId}/candidate-tags`);
const deleteAudienceCandidateSegment = (audienceId, candidateSegmentId) =>
  api.delete(`api/v1/audiences/${audienceId}/candidate-segments/${candidateSegmentId}`);
const deleteAudienceCandidateTag = (audienceId, candidateTagId) =>
  api.delete(`api/v1/audiences/${audienceId}/candidate-tags/${candidateTagId}`);
const saveAudienceCandidateMetadata = (audienceId, metadata) =>
  api.post(`api/v1/audiences/${audienceId}/candidate-metadata`, metadata);
const applyAudienceCandidates = (audienceId) =>
  api.post(`api/v1/audiences/${audienceId}/apply-candidates`);
const downloadAudienceMembersCsv = (audienceId) =>
  api.get(`api/v1/audiences/${audienceId}/download`, {}, {
    headers: { Accept: "text/csv" },
  });

const getBusinessCustomerSummarySearch = (businessId, query, options) =>
  api.get("api/v1/business-customers/_search/" + businessId + "/summary" + query, options);
const getBusinessCustomersCsv = (businessId, query) =>
  api.get(`api/v1/business-customers/_search/${businessId}/export/csv${query || ""}`, {}, {
    headers: {
      Accept: "*/*",
    },
    "Content-Type": "text/csv",
  });

const getBusinessCustomerSummary = (businessId, options) => api.get(`api/v1/business-customers/_search/${businessId}/load-summary`, options);
const getCustomerSummary = (bcId) => api.get(`api/v1/business-customers/${bcId}/summary`)
const applyAudienceBatch = (audienceId, batchNo) =>
  api.put(`api/v1/audiences/apply/${audienceId}?batchNo=${encodeURIComponent(batchNo)}`);

const applyBusinessCustomerImport = (id, batchNo) => {
  if (batchNo != null && batchNo !== "") {
    return applyAudienceBatch(id, batchNo);
  }
  return api.put(`api/v1/customer-imports/apply/${id}`);
};
const getBusinessCustomerEntriesList = (importId) => api.get(`api/v1/customer-import-entries/imports/${importId}`);
const successfulCustomerPayments = (bcId, options) => api.get(`api/v1/payment-tracks/business-customer/${bcId}`, options);
const expiredCustomerPayments = (bcId, options) => api.get(`api/v1/payment-schedules/business-customer/${bcId}/expired`, options);
const getBusinessCustomerSubscriptionAmounts = (bcId) => api.get(`api/v1/business-customers/${bcId}/subscriptions/amounts`);
const linkPlanWithBusinessCustomer = (businessCustomerId, planId) =>
  api.patch(`api/v1/business-customers/${businessCustomerId}/plans/${planId}/link`);
const linkBusinessCustomerToPrimary = (secondaryBusinessCustomerId, primaryBusinessCustomerId, reason) =>
  api.patch(
    `api/v1/business-customers/${secondaryBusinessCustomerId}/link/${primaryBusinessCustomerId}`,
    null,
    reason ? { params: { reason } } : undefined
  );
const unlinkBusinessCustomerFromPrimary = (secondaryBusinessCustomerId) =>
  api.patch(`api/v1/business-customers/${secondaryBusinessCustomerId}/unlink`);
const getSecondaryBusinessCustomers = (bcId, options) =>
  api.get(`api/v1/business-customers/${bcId}/secondaries`, options);
const getSecondaryBusinessCustomersCount = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/secondaries/count`);
const getBusinessCustomerLinkedPlans = (bcId, options) =>
  api.get(`api/v1/business-customers/${bcId}/plans`, options);
const getBusinessCustomerLinkedPlansCount = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/plans/count`);

const getDailyCharts = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/timeseries/daily/${startDate}/${endDate}`);
const getWeeklyCharts = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/timeseries/weekly/${startDate}/${endDate}`);
const get3MonthlyCharts = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/timeseries/monthly/${startDate}/${endDate}`);
const get6MonthlyCharts = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/timeseries/monthly/${startDate}/${endDate}`);
const get12MonthlyCharts = (businessId, startDate, endDate) => api.get(`api/v1/businesses/${businessId}/timeseries/monthly/${startDate}/${endDate}`);

const getWallet = (walletId) => api.get("api/v1/wallets/" + walletId);
const getGlobalWallet = (businessId) => api.get(`api/v1/wallets/business/${businessId}/status`);
const getWalletStatistics = (businessId, options) => api.get(`api/v1/wallets/statistics/business/${businessId}`, options);
const getFavouriteWallets = (businessId) => api.get(`api/v1/plan-groups/favourites/${businessId}`);
const favouriteWallet = (id) => api.patch(`api/v1/plan-groups/favourite/${id}`);
const unfavouriteWallet = (id) => api.patch(`api/v1/plan-groups/unfavourite/${id}`);

const customerRecentPayments = (businessCustomerId, size) => api.get(`api/v1/payment-tracks/business-customer/${businessCustomerId}/recent/${size}`);
const customerMissedPayments = (businessCustomerId, size) => api.get(`api/v1/payment-schedules/business-customer/${businessCustomerId}/recent/missed/${size}`);
const customerSubscriptionActivity = (businessCustomerId, size) => api.get(`/api/v1/payment-schedules/business-customer/${businessCustomerId}/future/${size}`);

const getTransactionReport = (businessId, options) => api.get(`api/v1/businesses/${businessId}/plan-groups/transactions/summary`, options);
const getTransactionReportChart = (businessId, type, options) => api.get(`api/v1/businesses/${businessId}/plan-groups/${type}/transactions/series`, options);
const getTransactionCustomReport = (businessId, body) => api.post(`api/v1/businesses/${businessId}/settlements/report`, body);

const subscribeReport = (businessId, body) => api.post(`/api/v1/businesses/${businessId}/subscriptions/report`, body);

const subscriptionsExternalPayments = (businessId, subId, options) => api.get(`api/v1/payment-schedules/${businessId}/subscription/${subId}/external`, options);
const planGroupExternalPayments = (businessId, groupId, options) => api.get(`api/v1/payment-schedules/${businessId}/plan-group/${groupId}/external`, options);
const businessCustomerExternalPayments = (businessId, bcId, options) => api.get(`api/v1/payment-schedules/${businessId}/business-customer/${bcId}/external`, options);
const planExternalPayments = (businessId, planId, options) => api.get(`api/v1/payment-schedules/${businessId}/plan/${planId}/external`, options);

const beginOnboardingRequest = (businessId, paymentProvider) => api.patch(`api/v1/identity-verif-requests/${businessId}/begin-onboarding/${paymentProvider}`);

const getReportConfigurations = (businessId) =>
  api.get("api/v1/report-configurations", { businessId });
const createReportConfiguration = (reportConfiguration) =>
  api.post("api/v1/report-configurations", reportConfiguration);
const generateReportConfigurationXlsx = (configId) =>
  api.get(`api/v1/report-configurations/${configId}/generate.xlsx`, null, {
    responseType: "arraybuffer",
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });
const deleteReportConfiguration = (configId) =>
  api.delete(`api/v1/report-configurations/${configId}`);

const getTagGroupsGraphed = (businessId) => api.get(`api/v1/tag-groups/business/${businessId}/graphed`);
const createTagGroup = (tagGroup) => api.post("api/v1/tag-groups", tagGroup);
const updateTagGroup = (groupId, tagGroup) => api.put(`api/v1/tag-groups/${groupId}`, tagGroup);
const getTagGroups = (businessId, options) =>
  api.get(`api/v1/tag-groups/business/${businessId}`, options);
const searchTagGroupsByName = (businessId, name) =>
  api.get(`api/v1/tag-groups/${businessId}/search/${encodeURIComponent(name)}`);
const createTag = (tag) => api.post("api/v1/tags", tag);
const updateTag = (tagId, tag) => api.put(`api/v1/tags/${tagId}`, tag);
const getTags = (businessId) => api.get(`api/v1/tags/business/${businessId}`);
const associateSubscriberToTag = (tagId, bcId) => api.post(`api/v1/tags/${tagId}/associate/${bcId}`);
const bulkAssociateSubscriberToTag = (tagId, bcIds) => api.post(`api/v1/tags/${tagId}/associate/bulk`, { ids: bcIds });
const dissociateSubscriberFromTag = (tagId, bcId) => api.delete(`api/v1/tags/${tagId}/dissociate/${bcId}`);
const bulkDissociateSubscribersFromTag = (tagId, bcIds) => api.delete(`api/v1/tags/${tagId}/dissociate/bulk`, {}, { data: { ids: bcIds } });

const saveResponsibilities = (membershipId, payload) => api.post(`api/v1/responsibilities/${membershipId}`, payload);
const getSegmentResponsibilities = (membershipId) => api.get(`api/v1/responsibilities/${membershipId}/segment`);
const getTagResponsibilities = (membershipId) => api.get(`api/v1/responsibilities/${membershipId}/tag`);
const removeSegmentResponsibility = (membershipId, segmentResponsibilityId) =>
  api.delete(`api/v1/responsibilities/${membershipId}/segment/${segmentResponsibilityId}`);
const removeTagResponsibility = (membershipId, tagResponsibilityId) =>
  api.delete(`api/v1/responsibilities/${membershipId}/tag/${tagResponsibilityId}`);

const getPlanPaymentSummary = (businessId, startDate, endDate) => api.get(`api/v1/payment-tracks/${businessId}/summary/${startDate}/${endDate}`);
const getPlanReconciliationSummary = (businessId, startDate, endDate) => api.get(`api/v1/payment-tracks/${businessId}/reconciliation/${startDate}/${endDate}`);
const getExternalColleagueSummary = (businessId, startDate, endDate, membershipId) => {
  const query = membershipId != null && membershipId !== ''
    ? `?membershipId=${encodeURIComponent(membershipId)}`
    : '';
  return api.get(`api/v1/payment-schedules/${businessId}/external/summary/${startDate}/${endDate}${query}`);
};
const getExternalColleagueSummaryCsv = (businessId, startDate, endDate, membershipId) => {
  const query = membershipId != null && membershipId !== ''
    ? `?membershipId=${encodeURIComponent(membershipId)}`
    : '';
  return api.get(
    `api/v1/payment-schedules/${businessId}/external/summary/${startDate}/${endDate}/csv${query}`,
    {},
    {
      headers: { Accept: '*/*' },
      'Content-Type': 'text/csv',
    }
  );
};
const getExternalColleagueSummaryPdf = (businessId, membershipId, startDate, endDate, recordedById) => {
  const query = recordedById != null && recordedById !== ''
    ? `?recordedById=${encodeURIComponent(recordedById)}`
    : '';
  return api.get(
    `api/v1/payment-schedules/${businessId}/${membershipId}/external/summary/${startDate}/${endDate}/pdf${query}`,
    {},
    { responseType: 'blob' }
  );
};

const addWatchersToPlan = (watchers) => api.post(`api/v1/watchers`, watchers);
const removeWatcherFromPlan = (watcherId) => api.delete(`api/v1/watchers/${watcherId}`);
const getAllWatchersCount = (planId) => api.get(`api/v1/watchers/plan/${planId}/count`);
const getAllWatchers = (planId) => api.get(`api/v1/watchers/plan/${planId}`);



// ignite-jhipster-api-method-needle

// ------
// STEP 3
// ------
//
// Return back a collection of functions that we would consider our
// interface.  Most of the time it'll be just the list of all the
// methods in step 2.
//
// Notice we're not returning back the `api` created in step 1?  That's
// because it is scoped privately.  This is one way to create truly
// private scoped goodies in JavaScript.
//
export {
  // a list of the API functions from step 2
  createUser,
  updateUser,
  getUsers,
  getUser,
  deleteUser,
  createCountry,
  updateCountry,
  getCountries,
  getCountry,
  deleteCountry,
  createBusiness,
  updateBusiness,
  getBusinesses,
  getBusinessSummary,
  getBusiness,
  getMerchantAgreements,
  deleteBusiness,
  closeNotification,
  createPlan,
  updatePlan,
  getPlans,
  getPlan,
  getPlanQr,
  deletePlan,
  changePlanPrice,
  archivePlan,
  planPerformance,
  migratePlan,
  addProductWatcher,
  removeProductWatcher,
  productWatchers,
  productWatcherCount,
  getPlanPaymentMissed,
  getPlanPaymentMissedCount,
  getPlanPaymentPending,
  getPlanPaymentPendingCount,
  getPlanPaymentCollected,
  getPlanPaymentCollectedCount,
  createCustomer,
  updateCustomer,
  getCustomers,
  getCustomer,
  deleteCustomer,
  createSubscriber,
  updateSubscriber,
  updatePspFees,
  toggleReqsEnabled,
  toggleAllowDuplicatePhone,
  getSignupReqs,
  createSignupReqs,
  deleteSignupReq,
  getSignupReqExclusions,
  createSignupReqExclusions,
  deleteSignupReqExclusion,
  createSegment,
  updateSegment,
  getSegment,
  searchSegmentsByName,
  getSegmentCount,
  addSingleSubscriberToSegment,
  addBulkSubscriberToSegment,
  removeSubscriberFromSegment,
  removeBulkSubscribersFromSegment,
  getSubscribers,
  getSubscriber,
  getCountSubscribers,
  getCountSubscriptions,
  deleteSubscriber,
  getFilteredDataTotals,
  getPlanSubscribers,
  getPlanSubscribersCount,
  checkExclusivity,
  searchSubscribers,
  substituteSubscribersFilter,
  filterSubscribers,
  searchInvitations,
  dateFilterSubscribers,
  countSubscribers,
  getSubscriberMissedSchedules,
  cancelSubscription,
  changeSubscriptionTarget,
  addBulkListToSegment,
  pauseSubscription,
  countStatusSubscribers,
  getCountSearchSubscribers,
  getCountDateSearchSubscribers,
  subscribersBulkCancel,
  subscribersBulkPause,
  resumeSubscription,
  customerPendingPayments,
  businessCustomerMissedPayments,
  customerReceivedPayments,
  rescheduleSubscription,
  getCustomerReport,
  getSubscriptionSummary,
  getBusinessCustomerSummarySearch,
  getBusinessCustomersCsv,
  getBusinessCustomerSummary,
  getCustomerSummary,
  getCustomerCharts,
  createMembership,
  updateMembership,
  getAllPlanGroupsGraphed,
  getMemberships,
  getColleagues,
  getMembership,
  deleteMembership,
  resendMembershipInvite,
  getPaySwitchCredentials,
  getAutoBillingSupport,
  createPaySwitchCredentials,
  uploadProviderPrivateKey,
  updatePaySwitchMerchantId,
  updatePaySwitchCredentials,
  getPaymentCredentials,
  getBusinessCustomerSubscriptionAmounts,
  linkPlanWithBusinessCustomer,
  linkBusinessCustomerToPrimary,
  unlinkBusinessCustomerFromPrimary,
  getSecondaryBusinessCustomers,
  getSecondaryBusinessCustomersCount,
  getBusinessCustomerLinkedPlans,
  getBusinessCustomerLinkedPlansCount,
  getPaymentTrackCount,

  // ignite-jhipster-api-export-needle
  setAuthToken,
  removeAuthToken,
  getAccount,
  updateAccount,
  getDefMembership,
  setDefaultMembership,
  signupBusiness,
  generateVerificationCode,
  getMembershipInvite,
  getMembershipInvites,
  createMembershipInvite,
  updateMembershipInvite,
  deleteMembershipInvite,
  getSelfSignup,
  resendSelfSignup,
  resendBulkSelfSignup,
  getSelfSignups,
  getSelfSignupsFilter,
  searchInvitationsCount,
  externalPaymentBySubscriber,
  externalPaymentPerSubscription,
  triggerMomoPaymentByPlan,
  triggerMomoPaymentBySchedule,
  triggerMomoPaymentBySubscriber,
  fetchProviderAccount,
  verifyPaymentRequest,
  getCountSelfSignups,
  deleteSelfSignup,
  getInvitationUptake,
  downloadUptakePdf,
  downloadUptakeCsv,
  getProfile,
  updateProfile,
  resendVerificationEmail,
  getPaymentSchedule,
  getPaymentSchedules,
  reassignPaymentSchedulePlan,
  getPaymentScheduleRange,
  getPaymentScheduleCalendar,
  getPaymentScheduleCalendarDay,
  getPaymentScheduleProjection,
  getPaymentScheduleSummary,
  createPaymentSchedule,
  updatePaymentSchedule,
  deletePaymentSchedule,
  notifyPaymentSchedule,
  getSubscriptionMissedSchedule,
  getSubscriptionPendingSchedule,
  getRecentPendingPaymentScheduleForSubscription,
  getSubscriberRevenue,
  getPaymentTrack,
  getPaymentTrackGroup,
  getCollectedPaymentTrack,
  getMissedPaymentTrack,
  getExternalPaymentTrack,
  getPaymentTrackGroupCount,
  getPaymentTracks,
  getPaymentTrackRange,
  getPaymentTrackRevenue,
  updatePaymentTrack,
  deletePaymentTrack,
  rescheduleSubscriber,
  paymentMarkExternally,
  countPaymentTracks,
  getCollectedPaymentCSV,
  getCollectedPaymentPDF,
  getPaymentsSummaryPdf,
  getReconciliationSummaryPdf,
  getMissedPaymentCSV,
  getExternalPaymentCSV,
  getMissedPaymentPDF,
  getExternalPaymentPDF,
  getPaymentAttributions,
  getWallet,
  getGlobalWallet,
  getWalletStatistics,
  getFavouriteWallets,
  favouriteWallet,
  unfavouriteWallet,
  getSettlement,
  getSettlements,
  getAllSettlements,
  getSettlementSources,
  getSettlementsCount,
  getAllSettlementsCount,
  getSettlementSourcesGroup,
  getSettlementsStatusCount,
  createSettlement,
  updateSettlement,
  deleteSettlement,
  getPlanSettlements,
  getStructureSettlement,
  getPlanGroup,
  getPlanGroupQr,
  getPlanGroups,
  getPlanGroupsFilter,
  getPlanGroupsCount,
  getPlanGroupsGraphed,
  getPlanGroupsGraphedActive,
  getPlanGroupsGraphedArchived,
  createPlanGroup,
  updatePlanGroup,
  deletePlanGroup,
  archivePlanGroup,
  getPlanGroupTier,
  getPlanGroupPaymentMissed,
  getPlanGroupPaymentMissedCount,
  getPlanGroupPaymentPending,
  getPlanGroupPaymentPendingCount,
  getPlanGroupPaymentPaid,
  getPlanGroupPaymentPaidCount,
  getPlanGroupPerformance,
  getPlanBusinessPerformance,
  getPlanGroupPlansSummary,
  getPlanGroupCustomersSummary,
  getPlanGroupDuplicate,
  getPlanReport,
  getPlanReportChart,
  enablePlanReport,
  leaderBoard,
  removeLeaderBoard,
  getPayoutChannel,
  getPayoutChannels,
  getSavedPayoutChannels,
  getFavouriteChannels,
  setFavouritePayoutChannels,
  setUnfavouritePayoutChannels,
  createPayoutChannel,
  updatePayoutChannel,
  deletePayoutChannel,
  archivePayoutChannel,
  getFilteredPayoutChannels,
  getBank,
  getBanks,
  getLinkedSystemBanks,
  getBankBranch,
  getBankBranches,
  filterBanks,
  getCredentials,
  createCredentials,
  generateRequest,
  getWebhook,
  getWebhookEventTypes,
  createWebhook,
  updateWebhook,
  toggleWebhook,
  getDocuments,
  getOrganisationDocuments,
  createBusinessVerification,
  verifyDocument,
  reviewDocument,
  previewDocument,
  businessVerificationRequest,
  getBusinessCustomers,
  getFilteredBusinessCustomers,
  getExactFilteredBusinessCustomers,
  updateEnterpriseCustomers,
  getBusinessCustomerSubscriptions,
  getBusinessCustomerRecord,
  getBusinessCustomerSubscriptionsCount,
  getBusinessCustomerStatement,
  getBusinessCustomerStatementCsv,
  getBusinessCustomerStatementPdf,
  businessCustomerImport,
  businessCustomerCsvDownload,
  sendBulkInvite,
  createBusinessCustomer,
  getCustomerListInvitation,
  resendBulkInvitation,
  getBusinessCustomerImports,
  getBusinessCustomerImportsCount,
  getCustomerEnrichments,
  getCustomerEnrichmentItems,
  submitCustomerEnrichment,
  createAudience,
  getAudienceById,
  getAudienceBulkInvitations,
  getAudienceMembers,
  getAudienceBatches,
  addAudienceMember,
  removeAudienceMember,
  getAudienceCandidateSegments,
  getAudienceCandidateTags,
  deleteAudienceCandidateSegment,
  deleteAudienceCandidateTag,
  saveAudienceCandidateMetadata,
  applyAudienceCandidates,
  downloadAudienceMembersCsv,
  getBusinessCustomerEntriesList,
  getBusinessQr,
  getIndustries,
  applyBusinessCustomerImport,
  successfulCustomerPayments,
  expiredCustomerPayments,
  getDailyCharts,
  getWeeklyCharts,
  get3MonthlyCharts,
  get6MonthlyCharts,
  get12MonthlyCharts,
  getWeeklyPaymentScheduleSummary,
  get3MonthsPaymentScheduleSummary,
  get6MonthsPaymentScheduleSummary,
  get12MonthsPaymentScheduleSummary,
  customerRecentPayments,
  customerMissedPayments,
  customerSubscriptionActivity,
  getTransactionReportChart,
  getTransactionReport,
  subscribeReport,
  getTransactionCustomReport,
  subscriptionsExternalPayments,
  planGroupExternalPayments,
  businessCustomerExternalPayments,
  planExternalPayments,
  businessCustomersBulkPause,
  businessCustomersBulkCancel,
  businessCustomersSingleCancel,
  businessCustomersSinglePause,
  getSubscriptionMissedScheduleCount,
  uploadLogo,
  beginOnboardingRequest,
  getReportConfigurations,
  createReportConfiguration,
  generateReportConfigurationXlsx,
  deleteReportConfiguration,
  getTagGroupsGraphed,
  createTagGroup,
  updateTagGroup,
  updateTag,
  getTagGroups,
  searchTagGroupsByName,
  createTag,
  getTags,
  associateSubscriberToTag,
  bulkAssociateSubscriberToTag,
  dissociateSubscriberFromTag,
  bulkDissociateSubscribersFromTag,
  saveResponsibilities,
  getSegmentResponsibilities,
  getTagResponsibilities,
  removeSegmentResponsibility,
  removeTagResponsibility,
  getPlanPaymentSummary,
  getPlanReconciliationSummary,
  getExternalColleagueSummary,
  getExternalColleagueSummaryCsv,
  getExternalColleagueSummaryPdf,
  removeWatcherFromPlan,
  addWatchersToPlan,
  getAllWatchersCount,
  getAllWatchers,
  getAccountsPaymentSearch,
  getAccountsPaymentCSV,
  getAccountsPaymentPDF,
  getAccountsPaymentTotals,
  getExternalPaymentTotals
};
